/**
 * Common Card Hover Styles
 *
 * Reusable hover effect styles for template cards and similar components.
 * Matches the hover effect used in template version select MenuItem.
 *
 * Usage:
 *   import { getCardHoverStyles } from '../common/cardHoverStyles';
 *
 *   <Card sx={{
 *     ...getCardHoverStyles(theme),
 *     // other styles
 *   }}>
 */

/**
 * Get common card hover styles
 * @param {Object} theme - MUI theme object
 * @returns {Object} MUI sx styles object with hover effects
 */
export const getCardHoverStyles = theme => {
  return {
    // Base transition for smooth hover effects
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

    // Base shadow
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',

    // Base border
    border: '1px solid',
    borderColor: 'divider',

    // Hover state - matches template version select MenuItem hover effect
    '&:hover': {
      // Lift effect
      transform: 'translateY(-4px)',

      // Enhanced shadow for depth
      boxShadow:
        '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',

      // Border color change to primary
      borderColor: 'primary.main',

      // Subtle background color change (similar to MenuItem hover)
      backgroundColor:
        theme.palette.mode === 'dark'
          ? theme.palette.action.hover
          : theme.palette.background.paper,
    },
  };
};

/**
 * Get card hover styles with custom overrides
 * @param {Object} theme - MUI theme object
 * @param {Object} overrides - Custom style overrides for hover state
 * @returns {Object} MUI sx styles object with hover effects
 */
export const getCardHoverStylesWithOverrides = (theme, overrides = {}) => {
  const baseStyles = getCardHoverStyles(theme);

  return {
    ...baseStyles,
    '&:hover': {
      ...baseStyles['&:hover'],
      ...overrides,
    },
  };
};
